import {
  WSBadge,
  WSButton,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { IMemberClientRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { RequirementStatus } from "@wingspanhq/payments/dist/interfaces/eligibilityRequirement";
import { AxiosError } from "axios";
import React from "react";
import { SetupFlowRouteComponentProps } from "../../../components/SetupFlow/SetupFlow";
import { useSignDocument } from "../../../query/files/mutations";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useMemberClientsQuery } from "../../../query/payments/queries";
import { QUERY_USERS_ACTIVITIES } from "../../../query/users/keys";
import { useActivities } from "../../../query/users/queries";
import {
  getPaymentsSetupProgress,
  getRedactedUserName
} from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { usersService } from "../../../services/users";
import { pluralize } from "../../../utils/stringHelper";
import { PaymentsSetupLayout } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";
import { getHasPendingEligibilityRequirements } from "../../utils/setUp";
import styles from "./styles.module.scss";

export const SetupEligibilityRequirements: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const userId = useUserId();
  const activityQuery = useActivities(userId);
  const memberClientsQuery = useMemberClientsQuery();

  const [submit, submitMeta] = useWSMutation<any, AxiosError>(
    async () => {
      const paymentsSetupProgress = getPaymentsSetupProgress(
        activityQuery.data
      );

      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            ...paymentsSetupProgress,
            currentStep: 5
          }
        }
      });
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES],
      onSuccess: onNext
    }
  );

  return (
    <PaymentsSetupLayout onBack={onBack}>
      <WSQueries queries={{ memberClientsQuery }}>
        {({ memberClientsQuery: { data: memberClients } }) => {
          const hasPendingEligibilityRequirements = getHasPendingEligibilityRequirements(
            memberClients
          );

          return (
            <>
              <WSText.Heading2 mb="M">Review & Sign Documents</WSText.Heading2>

              {memberClients
                .filter(
                  memberClient =>
                    memberClient.eligibilityRequirements &&
                    memberClient.eligibilityRequirements.length > 0
                )
                .map(memberClient => (
                  <>
                    <WSText mb="M">
                      {getRedactedUserName(memberClient.client.user)} has
                      assigned {memberClient.eligibilityRequirements?.length}{" "}
                      {pluralize(
                        memberClient.eligibilityRequirements?.length || 0,
                        { one: "document", other: "documents" }
                      )}{" "}
                      to review. Need help? Contact{" "}
                      {getRedactedUserName(memberClient.client.user)}.
                    </WSText>

                    {(memberClient.eligibilityRequirements || []).map(
                      eligibilityRequirement => (
                        <EligibilityRequirementPreview
                          data={eligibilityRequirement}
                        />
                      )
                    )}
                  </>
                ))}

              <WSButton
                mb="S"
                onClick={submit}
                fullWidth
                name="next"
                loading={submitMeta.isLoading}
                disabled={hasPendingEligibilityRequirements}
              >
                Continue
              </WSButton>
            </>
          );
        }}
      </WSQueries>
    </PaymentsSetupLayout>
  );
};

const EligibilityRequirementPreview: React.FC<{
  data: IMemberClientRequirementResponse;
}> = ({ data }) => {
  const [sign, signMeta] = useSignDocument();

  return data.status !== RequirementStatus.Complete ? (
    <WSPanel mb="M" key={data.eligibilityRequirementId}>
      <WSFlexBox.CenterY
        justify="space-between"
        wrap="nowrap"
        onClick={() => {
          sign({ documentId: data.documentId });
        }}
      >
        <WSText.Heading4 mr="M">{data.document?.title}</WSText.Heading4>
        <WSButton.Link
          btnTextClassName={styles.reviewAndSignButtonText}
          loading={signMeta.isLoading}
        >
          Review & Sign
        </WSButton.Link>
      </WSFlexBox.CenterY>
    </WSPanel>
  ) : (
    <WSPanel mb="M">
      <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
        <WSText.Heading4 mr="M" color="grey500">
          {data.document?.title}
        </WSText.Heading4>
        <WSBadge leftIcon={{ name: "successMark" }} text="Complete" />
      </WSFlexBox.CenterY>
    </WSPanel>
  );
};
